import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ChainedBackend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

// The locale a first-time visitor gets: Swedish when the browser prefers
// Swedish, English otherwise. A saved choice (or the profile setting applied
// after login) always wins over this.
export const browserLocale = (): "sv_SE" | "en_US" => {
    const preferred = typeof navigator !== "undefined"
        ? (navigator.languages?.length ? navigator.languages : [navigator.language])
        : [];
    return preferred.some((l) => l?.toLowerCase().startsWith("sv")) ? "sv_SE" : "en_US";
};

let lang = localStorage.getItem("locale");

if (!lang || lang === "null") {
    lang = browserLocale();
    localStorage.setItem("locale", lang);
}

// Cache-busting version tied to the build. `__APP_VERSION__` is injected by
// Vite (from BUILD_NUMBER); it changes on every production build, so a new
// deploy invalidates both the localStorage copy and the browser's HTTP cache
// of the JSON files — fixing stale translations after a release.
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

// In dev the build version is the constant 'dev', so the localStorage cache
// would serve a stale copy of the JSON for up to 24h and newly added keys
// wouldn't appear. Use a per-load version in dev so edits show immediately.
const cacheVersion = import.meta.env.DEV ? `dev-${Date.now()}` : appVersion;

i18n
    .use(ChainedBackend)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en_US',
        lng: lang,
        ns: ['messages', 'privacy-policy'], // load both namespaces
        defaultNS: 'messages',
        backend: {
            backends: [LocalStorageBackend, HttpBackend],
            backendOptions: [
                // localStorage cache is invalidated whenever the version changes.
                { expirationTime: 24 * 60 * 60 * 1000, defaultVersion: cacheVersion },
                // ?v=<build> busts the browser HTTP cache for the JSON files.
                { loadPath: `/locales/{{lng}}/{{ns}}.json?v=${encodeURIComponent(cacheVersion)}` },
            ],
        },
        react: {
            useSuspense: false,
            bindI18n: 'languageChanged loaded',
        }
    });

// Add a language change handler
i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
    localStorage.setItem("locale", lng);
});

export default i18n;